import { ref, onUnmounted, type Ref } from 'vue'

export interface ViewabilityOptions {
  threshold?: number
  viewTime?: number
  rootMargin?: string
  trackMultiple?: boolean
}

export interface ViewabilityTracker {
  isVisible: Ref<boolean>
  hasTrackedViewable: Ref<boolean>
  startTracking: (element: HTMLElement | null) => void
  stopTracking: () => void
  reset: () => void
}

export function useViewabilityTracker(
  onViewable: () => void,
  options: ViewabilityOptions = {}
): ViewabilityTracker {
  const {
    threshold = 0.5, // 50% visible
    viewTime = 1000, // 1 second
    rootMargin = '0px',
    trackMultiple = false,
  } = options

  const isVisible = ref(false)
  const hasTrackedViewable = ref(false)

  let observer: IntersectionObserver | null = null
  let viewableTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Handle intersection changes
   */
  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const entry = entries[0]
    const wasVisible = isVisible.value

    // Update visibility state
    isVisible.value =
      entry.isIntersecting && entry.intersectionRatio >= threshold

    if (isVisible.value && !wasVisible) {
      // Start the viewability timer
      if (!viewableTimer && (!hasTrackedViewable.value || trackMultiple)) {
        viewableTimer = setTimeout(() => {
          if (isVisible.value && (!hasTrackedViewable.value || trackMultiple)) {
            hasTrackedViewable.value = true
            onViewable()
          }
          viewableTimer = null
        }, viewTime)
      }
    }

    // Element became less visible
    else if (!isVisible.value && wasVisible) {
      // Clear the timer if element is no longer sufficiently visible
      if (viewableTimer) {
        clearTimeout(viewableTimer)
        viewableTimer = null
      }
    }
  }
  const startTracking = (element: HTMLElement | null) => {
    if (!element) return

    stopTracking()

    observer = new IntersectionObserver(handleIntersection, {
      threshold: [0, threshold, 1], // Track 0%, threshold%, and 100%
      rootMargin,
    })

    observer.observe(element)
  }

  const stopTracking = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }

    if (viewableTimer) {
      clearTimeout(viewableTimer)
      viewableTimer = null
    }
  }

  const reset = () => {
    hasTrackedViewable.value = false
    isVisible.value = false

    if (viewableTimer) {
      clearTimeout(viewableTimer)
      viewableTimer = null
    }
  }

  onUnmounted(() => {
    stopTracking()
  })

  return {
    isVisible,
    hasTrackedViewable,
    startTracking,
    stopTracking,
    reset,
  }
}
